// "Für die ganze Gruppe freigeben" (ADR-0064 Ziffer 4/5 mit Nachtrag S4a,
// ST-SEC-001 S4b). REINES MODELL, kein React, kein DOM; die Antwort liest der
// Befehls-Client.
//
// Die Freigabe ist nicht umkehrbar. Der Eintrag erscheint nur, wo der Server
// sie annehmen koennte (Hauskontakt, beschreibbarer Zustand); OB die Rolle
// freigeben darf, entscheidet allein die Policy am Server -- ihr 403 hat
// deshalb einen eigenen Satz statt eines vorab ausgeblendeten Eintrags.

import type { PlainMessage } from './access.ts';
import { sendCommand, type CommandMessages, type CommandOutcome, type SendLike } from './command-client.ts';
import { artWort, type HausArt } from './haus-art.ts';
import type { PartyDetail } from './parties.ts';
import { isWritableLifecycle, PARTY_CONFIRM_MESSAGES } from './party-lifecycle.ts';

export const SHARE_LABEL = 'Für die ganze Gruppe freigeben …';
export const SHARE_TITLE = 'Für die ganze Gruppe freigeben?';
export const SHARE_BUTTON = 'Freigeben';
export const ALREADY_SHARED = 'Der Kontakt war bereits für die ganze Gruppe sichtbar.';

export interface PartyShared {
  readonly lockVersion: number;
  /** `false`: der Kontakt war schon fuer alle Haeuser sichtbar; nichts wurde geschrieben. */
  readonly changed: boolean;
}

/** Bietet die Seite die Freigabe an? Ohne `visibility` (aelteres Image) nie. */
export function shareOffered(party: Pick<PartyDetail, 'visibility' | 'lifecycleStatus'>): boolean {
  return party.visibility?.scope === 'house' && isWritableLifecycle(party.lifecycleStatus);
}

/** Der Hausname ungekuerzt; ohne Namen die Art des eigenen Hauses (A2). */
function vonHaus(haus: string | null, art?: HausArt | null): string {
  return haus ?? artWort(art, 'deinem');
}

function fuerHaus(haus: string | null, art?: HausArt | null): string {
  return haus ?? artWort(art, 'deinen');
}

/** Die drei Absaetze des Dialogs: was sichtbar wird, was beim Haus bleibt, nicht umkehrbar. */
export function shareDialogText(haus: string | null, art?: HausArt | null): { readonly sichtbar: string; readonly bleibt: string; readonly endgueltig: string } {
  return {
    sichtbar: 'Danach finden alle in der Gruppe diesen Kontakt und sehen Name, Status, Kennungen, Beziehungen und Stichworte.',
    // Zeilen nach dem Wechsel sind gruppenweit (ADR-0064 Ziffer 5): eine echte
    // Datenschutzfolge, sie gehoert in die Entscheidung (CM-UX-004).
    bleibt: `Kontaktwege, Anschriften, Notizen und Aufgaben von ${vonHaus(haus, art)} bleiben nur für ${fuerHaus(haus, art)} sichtbar. Kontaktwege und Anschriften, die danach hinzukommen, sieht die ganze Gruppe.`,
    endgueltig: 'Die Freigabe lässt sich nicht zurücknehmen.',
  };
}

/** Die Wirkung, nicht die Handlung (CM-UX-004). */
export function shareConfirmation(applied: PartyShared, haus: string | null, art?: HausArt | null): string {
  return applied.changed
    ? `Der Kontakt ist jetzt für die ganze Gruppe sichtbar. Kontaktwege und Anschriften von ${vonHaus(haus, art)} bleiben bei ${vonHaus(haus, art)}.`
    : ALREADY_SHARED;
}

/** Je Grund ein Satz mit naechstem Schritt (NFR-UX-005); 404 wie ueberall neutral. */
export function shareMessages(haus: string | null, art?: HausArt | null): CommandMessages {
  return Object.freeze({
    notFound: PARTY_CONFIRM_MESSAGES.notFound,
    conflictFallback: Object.freeze({
      headline: 'Jemand anderes war schneller.',
      explanation: 'Der Kontakt hat sich geändert, seit du die Seite geöffnet hast. Er wurde nicht freigegeben.',
      nextStep: 'Die Seite zeigt jetzt den aktuellen Stand. Prüfe ihn und gib den Kontakt dann noch einmal frei.',
      retryable: false,
    }),
    conflicts: Object.freeze({
      party_share_conflict: Object.freeze({
        headline: 'Der Kontakt wurde nicht freigegeben.',
        explanation:
          'Bei den Kontaktwegen oder Anschriften ist für denselben Zweck zweimal „bevorzugt“ gesetzt. Vor der Freigabe muss das eindeutig sein.',
        nextStep:
          'Öffne „Kontaktwege pflegen“ oder „Anschriften pflegen“, setze bei einem der beiden den doppelten Zweck auf „Nicht mehr verwenden“ und gib den Kontakt dann noch einmal frei.',
        retryable: false,
      }),
      party_not_writable: Object.freeze({
        headline: 'Dieser Kontakt lässt sich nicht freigeben.',
        explanation: 'Er wurde inzwischen archiviert, zusammengeführt oder anonymisiert.',
        nextStep: 'Die Seite zeigt jetzt seinen Stand.',
        retryable: false,
      }),
    }),
    // Kein Rollencode im Satz (NFR-UX-003).
    forbidden: Object.freeze({
      headline: 'Freigeben darfst du diesen Kontakt nicht.',
      explanation: `Freigeben kann nur, wer in ${vonHaus(haus, art)} Kontakte erfassen darf. Der Kontakt bleibt nur für ${fuerHaus(haus, art)} sichtbar.`,
      nextStep: `Bitte deine Administration um die Berechtigung zum Erfassen in ${vonHaus(haus, art)}.`,
      retryable: false,
    }) satisfies PlainMessage,
  }) satisfies CommandMessages;
}

export function shareParty(options: {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly partyId: string;
  readonly lockVersion: number;
  readonly ownerHouseName: string | null;
  readonly ownerHouseKind?: HausArt | null;
  readonly send?: SendLike;
}): Promise<CommandOutcome<PartyShared>> {
  return sendCommand({
    url: `${options.baseUrl}/parties/${encodeURIComponent(options.partyId)}/share`,
    accessToken: options.accessToken,
    // confirmShare steht fest: die Rueckfrage ist der Dialog davor.
    body: { lockVersion: options.lockVersion, confirmShare: true },
    messages: shareMessages(options.ownerHouseName, options.ownerHouseKind),
    applied: (record) => ({
      lockVersion: typeof record.lockVersion === 'number' ? record.lockVersion : 0,
      changed: record.changed === true,
    }),
    ...(options.send === undefined ? {} : { send: options.send }),
  });
}
